import React, { useState, useEffect, useContext } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { formatCurrency, formatDateRu } from '../utils/formatUtils';
import { useTimezone } from '../contexts/TimezoneContext';
import MultiSelect from './MultiSelect';
import Pagination from './Pagination';
import { 
  DollarSign, 
  Filter, 
  Calendar, 
  User,
  Eye
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HistoryPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { formatDateTime } = useTimezone();
  const [deposits, setDeposits] = useState([]);
  const { tt } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [managers, setManagers] = useState([]);
  const [clients, setClients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    deposit_types: [], // Изменено на массив для множественного выбора
    managers: [], // Изменено на массив для множественного выбора
    date_from: '',
    date_to: ''
  });

  const ITEMS_PER_PAGE = 25;

  const depositTypes = [
    { value: 'ftd', label: 'FTD', color: 'green' },
    { value: 'reload', label: 'Reload', color: 'blue' },
    { value: 'deposit', label: tt('Депозит'), color: 'gray' }
  ];

  useEffect(() => {
    fetchDeposits();
    fetchClients();
    if (user?.role === 'superadmin') {
      fetchManagers();
    }
  }, []);


  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.deposit_types, filters.managers, filters.date_from, filters.date_to]);


  const fetchDeposits = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/deposits`);
      setDeposits(response.data);
    } catch (error) {
      console.error('Error fetching deposits:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await axios.get(`${API}/staff`);
      // Фильтруем только сотрудников (не клиентов)
      const staffOnly = response.data.filter(user => 
        user.role !== 'client' && 
        (user.role === 'sales_manager' || 
         user.role === 'retention_manager' || 
         user.role === 'team_lead_sales' || 
         user.role === 'team_lead_retention' ||
         user.role === 'admin' ||
         user.role === 'superadmin')
      );
      setManagers(staffOnly);
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const fetchClients = async () => {
    try {
      // OPTIMIZED: Load 1000 instead of 10000 for faster performance
      const response = await axios.get(`${API}/clients?skip=0&limit=1000`);
      setClients(response.data.items || response.data); // Support both formats
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleFilter = () => {
    fetchDeposits();
  };

  const clearFilters = () => {
    setFilters({
      deposit_types: [],
      managers: [],
      date_from: '',
      date_to: ''
    });
  };

  // Используем formatDateTime из TimezoneContext

  const getDepositTypeBadge = (type) => {
    const depositType = depositTypes.find(t => t.value === type);
    if (!depositType) return <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">{tt('Депозит')}</span>;
    
    const colorClasses = {
      green: 'bg-green-100 text-green-800',
      blue: 'bg-blue-100 text-blue-800',
      gray: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClasses[depositType.color]}`}>
        {depositType.label}
      </span>
    );
  };

  // Apply filters with multiple selection support
  const filteredDeposits = deposits.filter(deposit => {
    let matches = true;
    
    // Multiple deposit type filter
    if (filters.deposit_types.length > 0 && !filters.deposit_types.includes(deposit.deposit_type)) {
      matches = false;
    }
    
    // Multiple manager filter - фильтруем по тому кто СОЗДАЛ депозит
    if (filters.managers.length > 0) {
      // Приводим типы для сравнения (могут быть числа или строки)
      const depositCreator = String(deposit.created_by);
      const selectedManagers = filters.managers.map(m => String(m));
      
      if (!selectedManagers.includes(depositCreator)) {
        matches = false;
      }
    }
    
    if (filters.date_from) {
      const depositDate = new Date(deposit.created_at);
      const fromDate = new Date(filters.date_from);
      if (depositDate < fromDate) matches = false;
    }
    
    if (filters.date_to) {
      const depositDate = new Date(deposit.created_at);
      const toDate = new Date(filters.date_to);
      toDate.setHours(23, 59, 59, 999); // End of day
      if (depositDate > toDate) matches = false;
    }
    
    return matches;
  });

  // Pagination
  const paginatedDeposits = filteredDeposits.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Calculate statistics
  const totalAmount = filteredDeposits.reduce((sum, deposit) => sum + (deposit.amount || 0), 0);
  const ftdCount = filteredDeposits.filter(d => d.deposit_type === 'ftd').length;
  const reloadCount = filteredDeposits.filter(d => d.deposit_type === 'reload').length;

  return (
    <div className="page-container">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{tt('История депозитов')}</h1>
          <p className="page-subtitle">{tt('Полная история всех депозитов в системе')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
          
          {/* Deposit Type Filter */}
          <div>
            <label className="form-label">{tt('Тип депозита')}</label>
            <MultiSelect
              options={depositTypes.map(type => ({ value: type.value, label: type.label }))}
              selected={filters.deposit_types}
              onChange={(selected) => setFilters({...filters, deposit_types: selected})}
              placeholder={tt('Все типы')}
              data-testid="filter-deposit-type"
            />
          </div>

          {/* Manager Filter - Only for superadmin */}
          {user?.role === 'superadmin' && (
            <div>
              <label className="form-label">{tt('Менеджер')}</label>
              <MultiSelect
                options={managers.map(manager => ({ 
                  value: manager.id, 
                  label: manager.full_name || manager.name 
                }))}
                selected={filters.managers}
                onChange={(selected) => setFilters({...filters, managers: selected})}
                placeholder={tt('Все менеджеры')}
                data-testid="filter-manager"
              />
            </div>
          )}

          {/* Date From */}
          <div>
            <label className="form-label">{tt('От')}</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({...filters, date_from: e.target.value})}
              className="form-input"
              data-testid="filter-date-from"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="form-label">{tt('До')}</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({...filters, date_to: e.target.value})}
              className="form-input"
              data-testid="filter-date-to"
            />
          </div>

          {/* Filter Button */}
          <div>
            <label className="form-label invisible">.</label>
            <button
              onClick={handleFilter}
              className="btn btn-primary w-full"
              data-testid="apply-filters"
            >
              <Filter className="w-4 h-4 mr-2" />
              {tt('Применить')}
            </button>
          </div>

          {/* Clear Filters */}
          <div>
            <label className="form-label invisible">.</label>
            <button
              onClick={clearFilters}
              className="btn btn-secondary w-full"
              data-testid="clear-filters"
            >
              {tt('Очистить')}
            </button>
          </div>

        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <div className="text-2xl font-bold text-blue-600">{filteredDeposits.length}</div>
          <div className="text-sm text-gray-600">{tt('Всего депозитов')}</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-green-600">{ftdCount}</div>
          <div className="text-sm text-gray-600">{tt('FTD депозитов')}</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-purple-600">{reloadCount}</div>
          <div className="text-sm text-gray-600">{tt('Reload депозитов')}</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-indigo-600">
            {formatCurrency(totalAmount, true)}
          </div>
          <div className="text-sm text-gray-600">{tt('Общая сумма')}</div>
        </div>
      </div>

      {/* Deposits Table */}
      <div className="card overflow-hidden" data-testid="deposits-table">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="spinner mr-3"></div>
            <span className="text-gray-600">{tt('Загрузка депозитов...')}</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{tt('Клиент')}</th>
                  <th>{tt('Тип')}</th>
                  <th>{tt('Сумма')}</th>
                  <th>{tt('Дата')}</th>
                  <th>{tt('Создатель депозита')}</th>
                  {user?.role === 'superadmin' && <th>{tt('Назначенный менеджер')}</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedDeposits.length === 0 ? (
                  <tr>
                    <td colSpan={user?.role === 'superadmin' ? 6 : 5} className="text-center py-12 text-gray-500">
                      <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>{tt('Депозиты не найдены')}</p>
                    </td>
                  </tr>
                ) : (
                  paginatedDeposits.map((deposit) => {
                    // Find client by ID to get assigned manager
                    const client = clients.find(c => c.id === deposit.client_id);
                    const assignedManager = managers.find(m => m.id === client?.assigned_manager);
                    
                    const handleRowClick = (e) => {
                      // Проверяем что клик НЕ на ссылке
                      const isLink = e.target.tagName === 'A' || e.target.closest('a');
                      
                      if (!isLink) {
                        navigate(`/crm/clients/${deposit.client_id}`);
                      }
                    };
                    
                    return (
                      <tr 
                        key={deposit.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={handleRowClick}
                      >
                        <td>
                          <div>
                            <Link 
                              to={`/crm/clients/${deposit.client_id}`}
                              className="font-medium text-blue-600 hover:text-blue-800"
                            >
                              {deposit.client_name || client?.name || `${tt('Клиент')} #${deposit.client_id}`}
                            </Link>
                            <div className="text-sm text-gray-500">ID: {deposit.client_id}</div>
                          </div>
                        </td>
                        <td>
                          {getDepositTypeBadge(deposit.deposit_type)}
                        </td>
                        <td>
                          <div className="font-semibold text-green-600">
                            +{formatCurrency(deposit.amount || 0, true)}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            {formatDateTime(deposit.created_at)}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center text-sm text-blue-600">
                            <User className="w-4 h-4 mr-2" />
                            {deposit.created_by_name || tt('Неизвестно')}
                          </div>
                        </td>
                        {user?.role === 'superadmin' && (
                          <td>
                            <div className="flex items-center text-sm text-gray-600">
                              <User className="w-4 h-4 mr-2" />
                              {assignedManager?.name || tt('Не назначен')}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={filteredDeposits.length}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default HistoryPage;